import {Popover, Tag} from "antd";
import React from "react";
import SbomProject from "../../domain/SbomProject";
import SbomProjectVersion from "../../domain/SbomProjectVersion";
import {colorForSbomProjectVersionStatus, formatSbomProjectVersionStatus, SbomProjectVersionStatus} from "../../domain/SbomProjectVersionStatus";
import SbomComponentFinding from "../../domain/SbomComponentFinding";
import SingleResult from "../../service/SingleResult";
import FindingAnalysisStatistics, {AnalysisStyle} from "./FindingAnalysisStatistics";
import FindingStatistics from "./FindingStatistics";
import FindingCount from "./FindingCount";
import {useFocusedFindingAnalysisLauncher} from "./FocusedFindingAnalysisHost";
import * as styles from "./SbomProjectVersionStatistics.module.css";

interface SbomProjectVersionFindingStatisticsActionProps {
    projectVersion?: SbomProjectVersion;
    sbomProject?: SingleResult<SbomProject> | SbomProject;
    className?: string;
    onAfterFindingStatusSave?: (finding: SbomComponentFinding) => void;
}

function renderSbomProjectVersionFindingStatistics(sbomProjectVersion?: SbomProjectVersion) {
    switch (sbomProjectVersion?.status) {
        case SbomProjectVersionStatus.WAITING_FOR_SBOM:
        case SbomProjectVersionStatus.WAITING_FOR_EVALUATION:
        case SbomProjectVersionStatus.NOT_VULNERABLE:
            return <Tag color={colorForSbomProjectVersionStatus(sbomProjectVersion.status)}>
                {formatSbomProjectVersionStatus(sbomProjectVersion.status)}
            </Tag>;
        case SbomProjectVersionStatus.VULNERABLE: {
            const summary = sbomProjectVersion.findingSummary;
            const hasAnalysisImpact = summary.occurrenceCount !== summary.analysedOccurrenceCount;
            const statistics = <FindingStatistics findingStatistics={summary.analysedUniqueFindingStatistics} compact={true}/>;

            return <span className={styles.statistics}>
                {hasAnalysisImpact ? <Popover
                    title="Vulnerabilities before analysis"
                    content={<span className={styles.statistics}>
                        <FindingStatistics findingStatistics={summary.uniqueFindingStatistics} compact={true}/>
                        <FindingCount
                            count={summary.occurrenceCount}
                            componentCount={summary.componentCount}
                            className={styles.occurrences}/>
                    </span>}>
                    <span>{statistics}</span>
                </Popover> : statistics}
                <FindingCount
                    count={summary.analysedOccurrenceCount}
                    componentCount={summary.analysedComponentCount}
                    className={styles.occurrences}/>
            </span>;
        }
        default:
            return <i>unknown</i>;
    }
}

export function renderSbomProjectVersionAnalysisStatistics(sbomProjectVersion?: SbomProjectVersion) {
    return <FindingAnalysisStatistics
        analysisStatistics={sbomProjectVersion?.analysisStatistics}
        style={AnalysisStyle.PROGRESS}
    />;
}

export function SbomProjectVersionFindingStatisticsAction(props: SbomProjectVersionFindingStatisticsActionProps) {
    const content = renderSbomProjectVersionFindingStatistics(props.projectVersion);

    if (props.projectVersion?.status !== SbomProjectVersionStatus.VULNERABLE || props.projectVersion.id === undefined) {
        return <>{content}</>;
    }

    return <VulnerableSbomProjectVersionFindingStatisticsAction {...props} content={content}/>;
}

function VulnerableSbomProjectVersionFindingStatisticsAction(props: SbomProjectVersionFindingStatisticsActionProps & { content: React.ReactNode }) {
    const focusedFindingAnalysis = useFocusedFindingAnalysisLauncher();
    const opening = focusedFindingAnalysis.isOpeningProjectVersionAnalysis(props.projectVersion);

    return <span
        className={props.className}
        style={props.className === undefined ? {cursor: "pointer", display: "inline-block"} : undefined}
        role={"button"}
        tabIndex={0}
        aria-busy={opening}
        title={"Open vulnerability analysis"}
        onClick={event => {
            event.stopPropagation();

            openAnalysis();
        }}
        onKeyDown={event => {
            if (event.key !== "Enter" && event.key !== " ") {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            openAnalysis();
        }}>
        {props.content}
    </span>;

    function openAnalysis() {
        if (props.projectVersion === undefined || opening) {
            return;
        }

        focusedFindingAnalysis.openProjectVersionAnalysis({
            projectVersion: props.projectVersion,
            sbomProject: props.sbomProject,
            onAfterFindingStatusSave: props.onAfterFindingStatusSave
        });
    }
}
